import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  RefreshCw, Plus, Trash2, Play, Pause, Settings, 
  MessageSquare, Image, Video, Filter, Clock, 
  ArrowRight, Phone, Key, Shield, Loader2, Check,
  AlertTriangle, Send, Wifi, Radio
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReplicatorPage() {
  const [loading, setLoading] = useState(true);
  const [replicators, setReplicators] = useState([]);
  const [connections, setConnections] = useState([]);
  const [telegramSession, setTelegramSession] = useState(null);
  const [telegramGroups, setTelegramGroups] = useState([]);
  
  // Estados do modal de login Telegram
  const [showTelegramLogin, setShowTelegramLogin] = useState(false);
  const [telegramStep, setTelegramStep] = useState('phone'); // phone, code, password
  const [telegramPhone, setTelegramPhone] = useState('');
  const [telegramCode, setTelegramCode] = useState('');
  const [telegramPassword, setTelegramPassword] = useState('');
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [phoneCodeHash, setPhoneCodeHash] = useState('');
  
  // Estados do modal de criar/editar replicador
  const [showReplicatorModal, setShowReplicatorModal] = useState(false);
  const [editingReplicator, setEditingReplicator] = useState(null);
  const [replicatorForm, setReplicatorForm] = useState({
    name: '',
    telegram_sources: [], // IDs dos grupos/canais do Telegram
    whatsapp_connection_id: '',
    whatsapp_destinations: [], // Múltiplos grupos/canais do WhatsApp
    filter_type: 'all', // all, contains, not_contains
    filter_keywords: '',
    include_images: true,
    include_videos: true,
    delay_seconds: 5,
    active: true
  });
  
  // Estados para seleção de grupos WhatsApp
  const [whatsappGroups, setWhatsappGroups] = useState([]);
  const [whatsappChannels, setWhatsappChannels] = useState([]);
  const [loadingWhatsappGroups, setLoadingWhatsappGroups] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('nexus-token');
      
      // Buscar replicadores
      const repRes = await fetch(`${API_URL}/api/replicators`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (repRes.ok) {
        const data = await repRes.json();
        setReplicators(data.replicators || []);
      }
      
      // Buscar conexões WhatsApp
      const connRes = await fetch(`${API_URL}/api/connections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (connRes.ok) {
        const data = await connRes.json();
        const connected = data.filter(c => c.status === 'connected');
        setConnections(connected);
        
        // Auto-selecionar se tiver apenas 1 conexão
        if (connected.length === 1) {
          setReplicatorForm(prev => ({ ...prev, whatsapp_connection_id: connected[0].id }));
        }
      }
      
      // Verificar sessão do Telegram
      const tgRes = await fetch(`${API_URL}/api/telegram/session`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (tgRes.ok) {
        const data = await tgRes.json();
        setTelegramSession(data.session);
        if (data.session?.connected) {
          setTelegramGroups(data.groups || []);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // ===== TELEGRAM LOGIN =====
  const startTelegramLogin = () => {
    setShowTelegramLogin(true);
    setTelegramStep('phone');
    setTelegramPhone('');
    setTelegramCode('');
    setTelegramPassword('');
  };

  const sendTelegramCode = async () => {
    if (!telegramPhone) {
      toast.error('Digite o número do telefone');
      return;
    }
    
    setTelegramLoading(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${API_URL}/api/telegram/send-code`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone: telegramPhone })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setPhoneCodeHash(data.phone_code_hash);
        setTelegramStep('code');
        toast.success('Código enviado! Verifique seu Telegram');
      } else {
        toast.error(data.detail || 'Erro ao enviar código');
      }
    } catch (error) {
      toast.error('Erro ao conectar com o servidor');
    } finally {
      setTelegramLoading(false);
    }
  };

  const verifyTelegramCode = async () => {
    if (!telegramCode) {
      toast.error('Digite o código recebido');
      return;
    }
    
    setTelegramLoading(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${API_URL}/api/telegram/verify-code`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          phone: telegramPhone,
          code: telegramCode,
          phone_code_hash: phoneCodeHash
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (data.requires_2fa) {
          setTelegramStep('password');
          toast.info('Digite sua senha 2FA');
        } else {
          setTelegramSession(data.session);
          setTelegramGroups(data.groups || []);
          setShowTelegramLogin(false);
          toast.success('Telegram conectado com sucesso!');
        }
      } else {
        toast.error(data.detail || 'Código inválido');
      }
    } catch (error) {
      toast.error('Erro ao verificar código');
    } finally {
      setTelegramLoading(false);
    }
  };

  const verifyTelegram2FA = async () => {
    if (!telegramPassword) {
      toast.error('Digite sua senha 2FA');
      return;
    }
    
    setTelegramLoading(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${API_URL}/api/telegram/verify-2fa`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          phone: telegramPhone,
          password: telegramPassword
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setTelegramSession(data.session);
        setTelegramGroups(data.groups || []);
        setShowTelegramLogin(false);
        toast.success('Telegram conectado com sucesso!');
      } else {
        toast.error(data.detail || 'Senha inválida');
      }
    } catch (error) {
      toast.error('Erro ao verificar senha');
    } finally {
      setTelegramLoading(false);
    }
  };

  const disconnectTelegram = async () => {
    if (!confirm('Deseja desconectar sua conta do Telegram?')) return;
    
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${API_URL}/api/telegram/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setTelegramSession(null);
        setTelegramGroups([]);
        toast.success('Telegram desconectado');
      }
    } catch (error) {
      toast.error('Erro ao desconectar');
    }
  };

  // ===== REPLICADORES =====
  const fetchWhatsappGroups = async (connectionId) => {
    if (!connectionId) return;
    
    setLoadingWhatsappGroups(true);
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${API_URL}/api/bulk-send/groups/${connectionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // Separar grupos/comunidades de canais
        const allItems = data.groups || [];
        const groups = allItems.filter(g => !g.is_channel && !g.jid?.includes('@newsletter'));
        const channels = allItems.filter(g => g.is_channel || g.jid?.includes('@newsletter'));
        setWhatsappGroups(groups);
        setWhatsappChannels(channels);
      }
      
      // Também buscar canais
      const channelsRes = await fetch(`${API_URL}/api/replicators/whatsapp-channels/${connectionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (channelsRes.ok) {
        const channelsData = await channelsRes.json();
        if (channelsData.channels?.length > 0) {
          setWhatsappChannels(prev => [...prev, ...channelsData.channels]);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar grupos:', error);
    } finally {
      setLoadingWhatsappGroups(false);
    }
  };

  const openCreateModal = () => {
    setEditingReplicator(null);
    setReplicatorForm({
      name: '',
      telegram_sources: [],
      whatsapp_connection_id: '',
      whatsapp_destinations: [],
      filter_type: 'all',
      filter_keywords: '',
      include_images: true,
      include_videos: true,
      delay_seconds: 5,
      active: true
    });
    setWhatsappGroups([]);
    setWhatsappChannels([]);
    setShowReplicatorModal(true);
  };

  const openEditModal = (replicator) => {
    setEditingReplicator(replicator);
    setReplicatorForm({
      name: replicator.name,
      telegram_sources: replicator.telegram_sources || [],
      whatsapp_connection_id: replicator.whatsapp_connection_id,
      whatsapp_destinations: replicator.whatsapp_destinations || [],
      filter_type: replicator.filter_type || 'all',
      filter_keywords: replicator.filter_keywords || '',
      include_images: replicator.include_images !== false,
      include_videos: replicator.include_videos !== false,
      delay_seconds: replicator.delay_seconds || 5,
      active: replicator.active !== false
    });
    if (replicator.whatsapp_connection_id) {
      fetchWhatsappGroups(replicator.whatsapp_connection_id);
    }
    setShowReplicatorModal(true);
  };

  const saveReplicator = async () => {
    if (!replicatorForm.name) {
      toast.error('Digite um nome para o replicador');
      return;
    }
    if (replicatorForm.telegram_sources.length === 0) {
      toast.error('Selecione pelo menos um grupo/canal do Telegram');
      return;
    }
    if (!replicatorForm.whatsapp_connection_id) {
      toast.error('Selecione uma conexão WhatsApp');
      return;
    }
    if (replicatorForm.whatsapp_destinations.length === 0) {
      toast.error('Selecione pelo menos um grupo/canal WhatsApp de destino');
      return;
    }
    
    try {
      const token = localStorage.getItem('nexus-token');
      const url = editingReplicator 
        ? `${API_URL}/api/replicators/${editingReplicator.id}`
        : `${API_URL}/api/replicators`;
      
      const res = await fetch(url, {
        method: editingReplicator ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(replicatorForm)
      });
      
      if (res.ok) {
        toast.success(editingReplicator ? 'Replicador atualizado!' : 'Replicador criado!');
        setShowReplicatorModal(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao salvar');
      }
    } catch (error) {
      toast.error('Erro ao salvar replicador');
    }
  };

  const toggleReplicator = async (replicator) => {
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${API_URL}/api/replicators/${replicator.id}/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setReplicators(prev => prev.map(r => 
          r.id === replicator.id ? { ...r, active: data.active } : r
        ));
        toast.success(data.active ? 'Replicador ativado' : 'Replicador pausado');
      }
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const deleteReplicator = async (replicator) => {
    if (!confirm(`Excluir replicador "${replicator.name}"?`)) return;
    
    try {
      const token = localStorage.getItem('nexus-token');
      const res = await fetch(`${API_URL}/api/replicators/${replicator.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        setReplicators(prev => prev.filter(r => r.id !== replicator.id));
        toast.success('Replicador excluído');
      }
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const toggleTelegramSource = (groupId) => {
    setReplicatorForm(prev => {
      const sources = prev.telegram_sources.includes(groupId)
        ? prev.telegram_sources.filter(id => id !== groupId)
        : [...prev.telegram_sources, groupId];
      return { ...prev, telegram_sources: sources };
    });
  };

  const toggleWhatsappDestination = (jid) => {
    setReplicatorForm(prev => {
      const destinations = prev.whatsapp_destinations.includes(jid)
        ? prev.whatsapp_destinations.filter(id => id !== jid)
        : [...prev.whatsapp_destinations, jid];
      return { ...prev, whatsapp_destinations: destinations };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <RefreshCw className="w-6 h-6 text-primary" />
            Replicador
          </h1>
          <p className="text-muted-foreground text-sm">
            Replique mensagens do Telegram para o WhatsApp automaticamente
          </p>
        </div>
        
        <div className="flex gap-2">
          {telegramSession?.connected ? (
            <Button variant="outline" onClick={disconnectTelegram}>
              <Phone className="w-4 h-4 mr-2" />
              Desconectar Telegram
            </Button>
          ) : (
            <Button onClick={startTelegramLogin}>
              <Phone className="w-4 h-4 mr-2" />
              Conectar Telegram
            </Button>
          )}
          
          <Button onClick={openCreateModal} disabled={!telegramSession?.connected}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Replicador
          </Button>
        </div>
      </div>

      {/* Status do Telegram */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Send className="w-5 h-5" />
            Status do Telegram
          </CardTitle>
        </CardHeader>
        <CardContent>
          {telegramSession?.connected ? (
            <div className="flex items-center gap-4">
              <Badge variant="default" className="bg-green-500">
                <Check className="w-3 h-3 mr-1" />
                Conectado
              </Badge>
              <span className="text-sm text-muted-foreground">
                {telegramSession.phone} - {telegramGroups.length} grupos/canais disponíveis
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <Badge variant="secondary">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Desconectado
              </Badge>
              <span className="text-sm text-muted-foreground">
                Conecte sua conta do Telegram para criar replicadores
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista de Replicadores */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Radio className="w-5 h-5" />
            Meus Replicadores ({replicators.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {replicators.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <RefreshCw className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum replicador criado ainda</p>
              <p className="text-sm">Crie um para começar a replicar mensagens</p>
            </div>
          ) : (
            <div className="space-y-3">
              {replicators.map(rep => (
                <div 
                  key={rep.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{rep.name}</span>
                      <Badge variant={rep.active ? 'default' : 'secondary'}>
                        {rep.active ? 'Ativo' : 'Pausado'}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
                      <span>{rep.telegram_sources?.length || 0} fonte(s) Telegram</span>
                      <ArrowRight className="w-4 h-4" />
                      <span>{rep.whatsapp_destinations?.length || 1} destino(s) WhatsApp</span>
                    </div>
                    {rep.filter_type !== 'all' && (
                      <div className="text-xs text-muted-foreground mt-1">
                        <Filter className="w-3 h-3 inline mr-1" />
                        Filtro: {rep.filter_type === 'contains' ? 'Contém' : 'Não contém'} "{rep.filter_keywords}"
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      <Clock className="w-3 h-3 inline mr-1" />
                      Delay: {rep.delay_seconds}s
                      {rep.include_images && <span className="ml-2"><Image className="w-3 h-3 inline" /> Imagens</span>}
                      {rep.include_videos && <span className="ml-2"><Video className="w-3 h-3 inline" /> Vídeos</span>}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      size="icon" 
                      variant="ghost"
                      onClick={() => toggleReplicator(rep)}
                    >
                      {rep.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </Button>
                    <Button 
                      size="icon" 
                      variant="ghost"
                      onClick={() => openEditModal(rep)}
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="icon" 
                      variant="ghost"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => deleteReplicator(rep)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Login Telegram */}
      <Dialog open={showTelegramLogin} onOpenChange={setShowTelegramLogin}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Conectar Telegram
            </DialogTitle>
            <DialogDescription>
              {telegramStep === 'phone' && 'Digite seu número de telefone do Telegram'}
              {telegramStep === 'code' && 'Digite o código recebido no Telegram'}
              {telegramStep === 'password' && 'Digite sua senha de verificação em duas etapas'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {telegramStep === 'phone' && (
              <div className="space-y-2">
                <Label>Número do Telefone (com código do país)</Label>
                <Input
                  placeholder="+5511999999999"
                  value={telegramPhone}
                  onChange={(e) => setTelegramPhone(e.target.value)}
                />
              </div>
            )}
            
            {telegramStep === 'code' && (
              <div className="space-y-2">
                <Label>Código de Verificação</Label>
                <Input
                  placeholder="12345"
                  value={telegramCode}
                  onChange={(e) => setTelegramCode(e.target.value)}
                  maxLength={5}
                />
              </div>
            )}
            
            {telegramStep === 'password' && (
              <div className="space-y-2">
                <Label>Senha 2FA</Label>
                <Input
                  type="password"
                  placeholder="Sua senha de verificação"
                  value={telegramPassword}
                  onChange={(e) => setTelegramPassword(e.target.value)}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTelegramLogin(false)}>
              Cancelar
            </Button>
            {telegramStep === 'phone' && (
              <Button onClick={sendTelegramCode} disabled={telegramLoading}>
                {telegramLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Enviar Código
              </Button>
            )}
            {telegramStep === 'code' && (
              <Button onClick={verifyTelegramCode} disabled={telegramLoading}>
                {telegramLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Verificar
              </Button>
            )}
            {telegramStep === 'password' && (
              <Button onClick={verifyTelegram2FA} disabled={telegramLoading}>
                {telegramLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Confirmar
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Criar/Editar Replicador */}
      <Dialog open={showReplicatorModal} onOpenChange={setShowReplicatorModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingReplicator ? 'Editar Replicador' : 'Novo Replicador'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Nome */}
            <div className="space-y-2">
              <Label>Nome do Replicador</Label>
              <Input
                placeholder="Ex: Sinais Crypto"
                value={replicatorForm.name}
                onChange={(e) => setReplicatorForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>

            {/* Fontes Telegram */}
            <div className="space-y-2">
              <Label>Grupos/Canais do Telegram (Fonte)</Label>
              <p className="text-xs text-muted-foreground">Selecione de onde as mensagens serão copiadas</p>
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {telegramGroups.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground text-center">
                    Nenhum grupo/canal encontrado
                  </p>
                ) : (
                  telegramGroups.map(group => (
                    <div 
                      key={group.id}
                      className={`flex items-center justify-between p-3 border-b last:border-0 cursor-pointer hover:bg-muted/50 ${
                        replicatorForm.telegram_sources.includes(group.id) ? 'bg-primary/10' : ''
                      }`}
                      onClick={() => toggleTelegramSource(group.id)}
                    >
                      <div>
                        <span className="font-medium">{group.title}</span>
                        <Badge variant="outline" className="ml-2 text-[10px]">
                          {group.type}
                        </Badge>
                      </div>
                      {replicatorForm.telegram_sources.includes(group.id) && (
                        <Check className="w-4 h-4 text-primary" />
                      )}
                    </div>
                  ))
                )}
              </div>
              {replicatorForm.telegram_sources.length > 0 && (
                <p className="text-xs text-primary">{replicatorForm.telegram_sources.length} selecionado(s)</p>
              )}
            </div>

            {/* Destino WhatsApp */}
            <div className="space-y-2">
              <Label>Conexão WhatsApp</Label>
              <Select 
                value={replicatorForm.whatsapp_connection_id}
                onValueChange={(value) => {
                  setReplicatorForm(prev => ({ ...prev, whatsapp_connection_id: value, whatsapp_destinations: [] }));
                  fetchWhatsappGroups(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conexão" />
                </SelectTrigger>
                <SelectContent>
                  {connections.map(conn => (
                    <SelectItem key={conn.id} value={conn.id}>
                      {conn.name || conn.instance_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {replicatorForm.whatsapp_connection_id && (
              <div className="space-y-2">
                <Label>Grupos/Canais WhatsApp (Destino)</Label>
                <p className="text-xs text-muted-foreground">Selecione onde as mensagens serão enviadas</p>
                {loadingWhatsappGroups ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Carregando grupos e canais...
                  </div>
                ) : (
                  <div className="border rounded-lg max-h-48 overflow-y-auto">
                    {whatsappGroups.length === 0 && whatsappChannels.length === 0 ? (
                      <p className="p-4 text-sm text-muted-foreground text-center">
                        Nenhum grupo ou canal encontrado
                      </p>
                    ) : (
                      <>
                        {/* Grupos */}
                        {whatsappGroups.length > 0 && (
                          <>
                            <div className="px-3 py-2 bg-muted/50 text-xs font-medium text-muted-foreground sticky top-0">
                              Grupos ({whatsappGroups.length})
                            </div>
                            {whatsappGroups.map(group => {
                              const jid = group.jid || group.group_id;
                              return (
                                <div 
                                  key={jid}
                                  className={`flex items-center justify-between p-3 border-b cursor-pointer hover:bg-muted/50 ${
                                    replicatorForm.whatsapp_destinations.includes(jid) ? 'bg-primary/10' : ''
                                  }`}
                                  onClick={() => toggleWhatsappDestination(jid)}
                                >
                                  <div>
                                    <span className="font-medium">{group.name}</span>
                                    <span className="text-xs text-muted-foreground ml-2">
                                      ({group.participants_count || 0} membros)
                                    </span>
                                  </div>
                                  {replicatorForm.whatsapp_destinations.includes(jid) && (
                                    <Check className="w-4 h-4 text-primary" />
                                  )}
                                </div>
                              );
                            })}
                          </>
                        )}
                        
                        {/* Canais */}
                        {whatsappChannels.length > 0 && (
                          <>
                            <div className="px-3 py-2 bg-muted/50 text-xs font-medium text-muted-foreground sticky top-0">
                              Canais ({whatsappChannels.length})
                            </div>
                            {whatsappChannels.map(channel => {
                              const jid = channel.jid || channel.id;
                              return (
                                <div 
                                  key={jid}
                                  className={`flex items-center justify-between p-3 border-b cursor-pointer hover:bg-muted/50 ${
                                    replicatorForm.whatsapp_destinations.includes(jid) ? 'bg-primary/10' : ''
                                  }`}
                                  onClick={() => toggleWhatsappDestination(jid)}
                                >
                                  <div>
                                    <span className="font-medium">{channel.name || channel.title}</span>
                                    <Badge variant="outline" className="ml-2 text-[10px]">Canal</Badge>
                                  </div>
                                  {replicatorForm.whatsapp_destinations.includes(jid) && (
                                    <Check className="w-4 h-4 text-primary" />
                                  )}
                                </div>
                              );
                            })}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
                {replicatorForm.whatsapp_destinations.length > 0 && (
                  <p className="text-xs text-primary">{replicatorForm.whatsapp_destinations.length} destino(s) selecionado(s)</p>
                )}
              </div>
            )}

            {/* Filtros */}
            <div className="space-y-2">
              <Label>Filtro de Mensagens</Label>
              <Select 
                value={replicatorForm.filter_type}
                onValueChange={(value) => setReplicatorForm(prev => ({ ...prev, filter_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as mensagens</SelectItem>
                  <SelectItem value="contains">Apenas mensagens que CONTÊM palavra-chave</SelectItem>
                  <SelectItem value="not_contains">Apenas mensagens que NÃO CONTÊM palavra-chave</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {replicatorForm.filter_type !== 'all' && (
              <div className="space-y-2">
                <Label>Palavras-chave (separadas por vírgula)</Label>
                <Input
                  placeholder="Ex: bom dia, promoção, oferta"
                  value={replicatorForm.filter_keywords}
                  onChange={(e) => setReplicatorForm(prev => ({ ...prev, filter_keywords: e.target.value }))}
                />
              </div>
            )}

            {/* Opções */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  <span className="text-sm">Incluir Imagens</span>
                </div>
                <Switch
                  checked={replicatorForm.include_images}
                  onCheckedChange={(checked) => setReplicatorForm(prev => ({ ...prev, include_images: checked }))}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  <span className="text-sm">Incluir Vídeos</span>
                </div>
                <Switch
                  checked={replicatorForm.include_videos}
                  onCheckedChange={(checked) => setReplicatorForm(prev => ({ ...prev, include_videos: checked }))}
                />
              </div>
            </div>

            {/* Delay */}
            <div className="space-y-2">
              <Label>Delay entre mensagens (segundos)</Label>
              <Input
                type="number"
                min="1"
                max="300"
                value={replicatorForm.delay_seconds}
                onChange={(e) => setReplicatorForm(prev => ({ ...prev, delay_seconds: parseInt(e.target.value) || 5 }))}
              />
              <p className="text-xs text-muted-foreground">
                Tempo de espera antes de enviar cada mensagem replicada
              </p>
            </div>

            {/* Ativo */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <span className="font-medium">Ativar Replicador</span>
                <p className="text-xs text-muted-foreground">Iniciar replicação imediatamente</p>
              </div>
              <Switch
                checked={replicatorForm.active}
                onCheckedChange={(checked) => setReplicatorForm(prev => ({ ...prev, active: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReplicatorModal(false)}>
              Cancelar
            </Button>
            <Button onClick={saveReplicator}>
              {editingReplicator ? 'Salvar Alterações' : 'Criar Replicador'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
